import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

import { graphql } from 'gatsby'
import classNames from 'classnames'
import { Alert } from '@gymondo/frontend-core/components'

import { STATES } from '@gymondo/frontend-core/config/cookies.config'
import { StaticLayout as Layout } from '../../../component'
import helper from './helper/term-and-condition.helper'

import style from './term-and-condition.module.scss'

import { setCookieConsentState } from '../../../service/cookie-consent'
import useIsClient from '../../../hook/use-is-client/use-is-client.hook'

/**
 * Render list of navigation link to the left side of the screen
 * @param {object} list - list of object with title of the links
 */
export const renderNavigations = (list) => {
    const hasWindow = typeof window !== 'undefined'
    let chosenIndex =
        list &&
        list.findIndex(
            (item) =>
                hasWindow &&
                `#${item.link}` ===
                    (window && window.location && window.location.hash)
        )
    chosenIndex = chosenIndex > -1 ? chosenIndex : 0

    return (
        list &&
        list.map((term, index) => (
            <a
                href={`#${term.link}`}
                key={term.title}
                className={classNames(
                    style.linkButton,
                    'link',
                    style.clauseTitleSecondary,
                    chosenIndex === index && [
                        style.linkButtonActive,
                        'gym-h--tertiary',
                    ]
                )}
            >
                {term.title}
            </a>
        ))
    )
}

export const renderContent = (data, hideHeader, cookieSettings) => {
    const { html, links } = helper.treatFiles(data)

    return (
        <div
            className={classNames(
                'page-wrapper',
                {
                    [style.wrapperNoHeader]: hideHeader,
                },
                style.wrapper
            )}
        >
            <div className="row">
                {!hideHeader && (
                    <div
                        className={classNames(
                            style.left,
                            'hidden-sm',
                            'hidden-xs',
                            'col'
                        )}
                    >
                        {renderNavigations(links)}
                    </div>
                )}
                <div
                    className={classNames(
                        'col-xs col-sm col-md col-lg',
                        style.content
                    )}
                >
                    {cookieSettings}
                    <div
                        className="html"
                        // eslint-disable-next-line
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            </div>
        </div>
    )
}

/**
 * Render the terms and conditions bases on the input
 * @param {object} data - the title, the content [and template]
 */
export const Index = ({
    data,
    pageContext: { noHeader, showCookieSettings },
}) => {
    const isClient = useIsClient()
    const Component = noHeader ? 'div' : Layout

    const dispatch = useDispatch()
    const { isEU } = useSelector((state) => state.cookieConsent)

    const cookieSettings = showCookieSettings && isEU && isClient && (
        <Alert severity="info" className={style.cookieConsent}>
            <span>
                <FormattedMessage
                    id="cookie-settings.description"
                    values={{
                        title: (
                            <span className={style.bold}>
                                <FormattedMessage id="cookie-settings.title" />
                            </span>
                        ),
                        settings: (
                            <span
                                className="link"
                                onClick={() => {
                                    dispatch(
                                        setCookieConsentState(STATES.SETTINGS)
                                    )
                                }}
                            >
                                <FormattedMessage id="cookie-settings.link" />
                            </span>
                        ),
                    }}
                />
            </span>
        </Alert>
    )

    return (
        <Component>{renderContent(data, noHeader, cookieSettings)}</Component>
    )
}

Index.propTypes = {
    data: PropTypes.object.isRequired,
    pageContext: PropTypes.object.isRequired,
}

const TermsAndConditions = Index

export default TermsAndConditions

/**
 * Retrieve documents with the conditions (inside ${language}.json)
 * @param {[string]} names - an array containing the files to be loaded. Possible variation:
 * - 1 file: there is no template, just the file that should be printed
 * - 2 files: first file is the template, the others are the parameters
 *   to be replaced on the template
 * - > 2 files: not implemented
 */
export const query = graphql`
    query($paramFile: String!, $templateRegex: String!) {
        allMarkdownRemark(
            filter: { fileAbsolutePath: { regex: $templateRegex } }
        ) {
            edges {
                node {
                    html
                    headings {
                        value
                        depth
                    }
                }
            }
        }
        allFile(
            filter: {
                relativeDirectory: {
                    eq: "page/generic/term-and-condition/content"
                }
                name: { eq: $paramFile }
            }
        ) {
            edges {
                node {
                    childContentJson {
                        terms {
                            parameters {
                                title
                                name
                                company
                                website
                            }
                        }
                    }
                }
            }
        }
    }
`
